/**
 * Calendar Types
 * 
 * Matches D1 schema from cortex-api/migrations/002_create_user_schedules.sql
 * For offline mode, we'll use these types and sync to D1 later.
 */

export interface CalendarEvent {
  id: string;
  user_id?: string; // Optional for offline mode
  event_type: 'event' | 'task' | 'reminder' | 'meeting';
  title: string;
  description?: string;
  start_time: number; // Unix timestamp (milliseconds)
  end_time?: number; // Unix timestamp (milliseconds)
  timezone?: string;
  recurrence?: string; // e.g., "daily", "weekly", "monthly"
  location?: string;
  participants?: string; // JSON array
  status: 'scheduled' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  category?: string;
  reminder_minutes?: number;
  created_at: number;
  updated_at: number;
  completed_at?: number;
  metadata?: string; // JSON string
}

/**
 * Calendar day representation
 */
export interface CalendarDay {
  date: number; // Day of month (1-31)
  month: number; // Month (0-11)
  year: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

/**
 * Calendar state
 */
export interface CalendarState {
  year: number;
  month: number; // 0-11
  days: CalendarDay[];
  events: CalendarEvent[]; // All events (not just current month)
}

/**
 * Create a new calendar event
 */
export function createCalendarEvent(
  title: string,
  startTime: number,
  options: {
    event_type?: 'event' | 'task' | 'reminder' | 'meeting';
    description?: string;
    end_time?: number;
    timezone?: string;
    location?: string;
    priority?: 'low' | 'medium' | 'high';
    category?: string;
    reminder_minutes?: number;
  } = {}
): CalendarEvent {
  const now = Date.now();
  return {
    id: `event_${now}_${Math.random().toString(36).substr(2, 9)}`,
    event_type: options.event_type || 'event',
    title,
    description: options.description,
    start_time: startTime,
    end_time: options.end_time,
    timezone: options.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: options.location,
    priority: options.priority || 'medium',
    category: options.category,
    reminder_minutes: options.reminder_minutes,
    status: 'scheduled',
    created_at: now,
    updated_at: now
  };
}

/**
 * Update a calendar event
 */
export function updateCalendarEvent(
  event: CalendarEvent,
  updates: Partial<Omit<CalendarEvent, 'id' | 'created_at'>>
): CalendarEvent {
  return {
    ...event,
    ...updates,
    updated_at: Date.now()
  };
}

/**
 * Delete a calendar event (returns new array without the event)
 */
export function deleteCalendarEvent(
  events: CalendarEvent[],
  eventId: string
): CalendarEvent[] {
  return events.filter(e => e.id !== eventId);
}

/**
 * Get events for a specific day
 */
export function getEventsForDay(
  events: CalendarEvent[],
  year: number,
  month: number,
  date: number
): CalendarEvent[] {
  const dayStart = new Date(year, month, date).setHours(0, 0, 0, 0);
  const dayEnd = new Date(year, month, date).setHours(23, 59, 59, 999);
  
  return events.filter(event => {
    const eventTime = event.start_time;
    return eventTime >= dayStart && eventTime <= dayEnd;
  });
}

